import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import emailjs from "@emailjs/browser";
import contactVideo from "../assets/WebsiteRZandDPGIF.mp4";

const SERVICE_ID = String(process.env.REACT_APP_EMAILJS_SERVICE_ID || "");
const CONTACT_TEMPLATE_ID = String(
  process.env.REACT_APP_EMAILJS_TEMPLATE_ID || ""
);
const AUTO_REPLY_TEMPLATE_ID = String(
  process.env.REACT_APP_EMAILJS_AUTO_REPLY_TEMPLATE_ID || ""
);
const PUBLIC_KEY = String(process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "");

emailjs.init({
  publicKey: PUBLIC_KEY,
});

function ContactMe() {
  const { showToast } = useOutletContext();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !SERVICE_ID ||
      !CONTACT_TEMPLATE_ID ||
      !AUTO_REPLY_TEMPLATE_ID ||
      !PUBLIC_KEY
    ) {
      showToast("error", "EmailJS configuration is missing");
      return;
    }

    const templateParams = {
      name: String(formData.name),
      email: String(formData.email),
      message: String(formData.message),
      title: "Portfolio Contact Message",
      time: String(new Date().toLocaleString()),
    };

    try {
      setLoading(true);

      await Promise.all([
        emailjs.send(SERVICE_ID, CONTACT_TEMPLATE_ID, templateParams),
        emailjs.send(SERVICE_ID, AUTO_REPLY_TEMPLATE_ID, templateParams),
      ]);

      showToast("success", "Message sent successfully");

      setFormData({
        name: "",
        email: "",
        message: "",
      });
    } catch (error) {
      console.log("EmailJS RAW ERROR:", error);

      showToast(
        "error",
        error?.text || error?.message || "Failed to send message"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto min-h-[calc(100vh-96px)] max-w-7xl px-5 py-4">
      <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2 lg:items-center">
        <div className="w-full max-w-lg">
          <h1 className="text-4xl font-bold text-white">
            Contact Me
          </h1>
          <p className="mt-3 text-slate-400">
            Let’s connect for projects, collaborations, or opportunities.
          </p>

          <form
            onSubmit={handleSubmit}
            className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-md sm:p-8"
          >
            <div className="space-y-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-300">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-white/10 bg-slate-900/50 px-4 py-2.5 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-white/40 focus:bg-slate-900"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-300">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-white/10 bg-slate-900/50 px-4 py-2.5 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-white/40 focus:bg-slate-900"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-300">
                  Message
                </label>
                <textarea
                  name="message"
                  required
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full resize-none rounded-xl border border-white/10 bg-slate-900/50 px-4 py-2.5 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-white/40 focus:bg-slate-900"
                  placeholder="Write your message"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-8 w-full rounded-xl bg-white px-6 py-3 text-sm font-bold text-slate-950 transition-all hover:bg-slate-200 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>

        <div className="flex w-full items-center justify-center">
          <div className="relative aspect-video w-full overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
            <video
              src={contactVideo}
              autoPlay
              muted
              playsInline
              onEnded={(e) => {
                e.target.currentTime = 4;
              }}
              className="h-full w-full object-cover opacity-90"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContactMe;